import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import { API, useAuth } from '../App';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);

  const [loginData, setLoginData] = useState({
    phone: '',
    password: ''
  });

  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    business_name: '',
    contact_person: '',
    phone: '',
    gst_number: ''
  });
  const [gstError, setGstError] = useState('');
  const [pinError, setPinError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length < 10) {
      return 'Mobile number must be at least 10 digits';
    }
    return '';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Validate phone
    const phoneValidation = validatePhone(loginData.phone);
    if (phoneValidation) {
      toast.error(phoneValidation);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      login(response.data.token, response.data.user);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    }
  };

  const validateGST = (gst) => {
    if (!gst || gst.length !== 15) {
      return 'GST number must be exactly 15 characters';
    }
    const pattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!pattern.test(gst.toUpperCase())) {
      return 'Invalid GST format (e.g., 27AAPFU0939F1ZV)';
    }
    return '';
  };

  const handleGSTChange = (value) => {
    setRegisterData({ ...registerData, gst_number: value.toUpperCase() });
    if (value) {
      setGstError(validateGST(value));
    } else {
      setGstError('');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Phone validation
    const phoneValidation = validatePhone(registerData.phone);
    if (phoneValidation) {
      setPhoneError(phoneValidation);
      toast.error(phoneValidation);
      return;
    }
    
    // PIN validation
    if (!registerData.password || registerData.password.length !== 4 || !/^\d{4}$/.test(registerData.password)) {
      setPinError('PIN must be exactly 4 digits');
      toast.error('Please enter a valid 4-digit PIN');
      return;
    }
    
    // Final GST validation
    const gstValidation = validateGST(registerData.gst_number);
    if (gstValidation) {
      setGstError(gstValidation);
      toast.error(gstValidation);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      login(response.data.token, response.data.user);
      
      if (response.data.status === 'pending') {
        toast.success('Registration successful! Your account is pending admin approval.');
      } else {
        toast.success('Registration successful!');
      }
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration failed', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  return (
    <div className="min-h-screen py-12 bg-accent" data-testid="auth-page">
      <div className="max-w-md mx-auto px-6">
        <div className="bg-white border border-border p-8">
          <h1 className="text-3xl font-heading font-bold text-center mb-2">
            {isLogin ? 'Welcome Back' : 'Join Us'}
          </h1>
          <p className="text-center text-muted-foreground mb-8">
            {isLogin ? 'Login to your B2B account' : 'Create your B2B account'}
          </p>

          {/* Toggle */}
          <div className="flex space-x-2 mb-8 border border-border">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3 font-medium transition-colors ${
                isLogin ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-accent'
              }`}
              data-testid="login-tab"
            >
              Login
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3 font-medium transition-colors ${
                !isLogin ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-accent'
              }`}
              data-testid="register-tab"
            >
              Register
            </button>
          </div>

          {isLogin ? (
            <motion.form
              key="login"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onSubmit={handleLogin}
              className="space-y-6"
              data-testid="login-form"
            >
              <div>
                <label className="block text-sm font-medium mb-2">Mobile Number</label>
                <input
                  type="tel"
                  required
                  value={loginData.phone}
                  onChange={(e) => setLoginData({ ...loginData, phone: e.target.value.replace(/\D/g, '') })}
                  className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors"
                  placeholder="Enter your mobile number"
                  inputMode="numeric"
                  data-testid="login-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">4-Digit PIN</label>
                <div className="relative">
                  <input
                    type={showLoginPassword ? "text" : "password"}
                    required
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 pr-12 outline-none transition-colors"
                    placeholder="Enter your 4-digit PIN"
                    maxLength="4"
                    inputMode="numeric"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="login-toggle-password"
                  >
                    {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-3 font-medium transition-all duration-300"
                data-testid="login-submit"
              >
                Login
              </button>
            </motion.form>
          ) : (
            <motion.form
              key="register"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              onSubmit={handleRegister}
              className="space-y-6"
              data-testid="register-form"
            >
              <div>
                <label className="block text-sm font-medium mb-2">Mobile Number *</label>
                <input
                  type="tel"
                  required
                  value={registerData.phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setRegisterData({ ...registerData, phone: value });
                    if (value && value.length < 10) {
                      setPhoneError('Mobile number must be at least 10 digits');
                    } else {
                      setPhoneError('');
                    }
                  }}
                  className={`w-full border ${phoneError ? 'border-red-500' : 'border-border'} focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors`}
                  placeholder="Enter 10-digit mobile number"
                  inputMode="numeric"
                  data-testid="register-phone"
                />
                {phoneError && (
                  <p className="text-red-600 text-sm mt-1">{phoneError}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">This will be used for login</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">4-Digit PIN *</label>
                <div className="relative">
                  <input
                    type={showRegisterPassword ? "text" : "password"}
                    required
                    value={registerData.password}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setRegisterData({ ...registerData, password: value });
                      if (value && value.length !== 4) {
                        setPinError('PIN must be exactly 4 digits');
                      } else {
                        setPinError('');
                      }
                    }}
                    className={`w-full border ${pinError ? 'border-red-500' : 'border-border'} focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 pr-12 outline-none transition-colors`}
                    placeholder="Enter 4-digit PIN"
                    maxLength="4"
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="register-toggle-password"
                  >
                    {showRegisterPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {pinError && (
                  <p className="text-red-600 text-sm mt-1">{pinError}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">Create a 4-digit PIN for secure login</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Business Name *</label>
                <input
                  type="text"
                  required
                  value={registerData.business_name}
                  onChange={(e) => setRegisterData({ ...registerData, business_name: e.target.value })}
                  className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors"
                  placeholder="Your Business Name"
                  data-testid="register-business-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Contact Person *</label>
                <input
                  type="text"
                  required
                  value={registerData.contact_person}
                  onChange={(e) => setRegisterData({ ...registerData, contact_person: e.target.value })}
                  className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors"
                  placeholder="Full Name"
                  data-testid="register-contact-person"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email (Optional)</label>
                <input
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  className="w-full border border-border focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors"
                  placeholder="your@email.com"
                  data-testid="register-email"
                />
                <p className="text-xs text-muted-foreground mt-1">For order notifications (optional)</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  GST Number *
                </label>
                <input
                  type="text"
                  required
                  maxLength="15"
                  value={registerData.gst_number}
                  onChange={(e) => handleGSTChange(e.target.value)}
                  className={`w-full border ${gstError ? 'border-red-500' : 'border-border'} focus:border-primary focus:ring-1 focus:ring-primary bg-transparent py-3 px-4 outline-none transition-colors uppercase`}
                  placeholder="27AAPFU0939F1ZV"
                  data-testid="register-gst"
                />
                {gstError && (
                  <p className="text-red-600 text-sm mt-1">{gstError}</p>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  15-character GST number mandatory for B2B registration
                </p>
                <p className="text-xs text-luxury-gold mt-1 font-medium">
                  Your account requires admin approval after registration
                </p>
              </div>
              <button
                type="submit"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-3 font-medium transition-all duration-300"
                data-testid="register-submit"
              >
                Create Account
              </button>
            </motion.form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
